import { TFunction } from "i18next";

/*
 * The Keepo's field ("ramo de atuação"), saved in `site.category`:
 * - `segment/specialty` (e.g. "beauty/hairdresser") or just `segment` (e.g. "beauty");
 * - "other", with the user's own words in `site.categoryOther`;
 * - a legacy key from before segments (e.g. "photographer"), kept as it is on older Keepos.
 * Labels: `site.categoryTree.{segment}.label` / `.specialties.{specialty}`; legacy ones `site.categories.{key}`.
 */

export const OTHER_SEGMENT = "other";

export const CATEGORY_SEGMENTS: { key: string; specialties: string[] }[] = [
  {
    key: "beauty",
    specialties: ["hairdresser", "barbershop", "nails", "aesthetics", "makeup", "brows-lashes", "tattoo", "spa"],
  },
  {
    key: "health",
    specialties: [
      "doctor",
      "dentistry",
      "psychology",
      "nutrition",
      "physiotherapy",
      "speech-therapy",
      "clinic",
      "pharmacy",
    ],
  },
  { key: "fitness", specialties: ["personal-trainer", "gym", "yoga-pilates", "holistic", "sports"] },
  {
    key: "food",
    specialties: ["restaurant", "fast-food", "bakery-sweets", "delivery", "bar-drinks", "coffee", "homemade"],
  },
  { key: "fashion", specialties: ["clothing-store", "sewing", "shoes", "jewelry", "kids", "thrift"] },
  { key: "retail", specialties: ["store", "online-store", "reseller", "wholesale", "grocery"] },
  {
    key: "services",
    specialties: ["law", "accounting", "architecture", "marketing", "consulting", "real-estate", "insurance", "tech"],
  },
  { key: "home", specialties: ["construction", "repairs", "cleaning", "decor", "furniture", "gardening"] },
  { key: "education", specialties: ["tutor", "school", "languages", "infoproducts", "mentoring", "driving-school"] },
  {
    key: "creators",
    specialties: ["influencer", "musician", "photography", "design", "writing", "crafts", "video", "podcast"],
  },
  { key: "events", specialties: ["party-buffet", "weddings", "dj", "event-decor", "venue"] },
  { key: "pets", specialties: ["pet-shop", "vet", "grooming", "training", "pet-hotel"] },
  { key: "auto", specialties: ["mechanic", "car-detailing", "car-sales", "transport"] },
  { key: "travel", specialties: ["travel-agency", "lodging", "tour-guide"] },
  { key: "community", specialties: ["nonprofit", "religious", "association"] },
  { key: OTHER_SEGMENT, specialties: [] },
];

/** Keys from before segments (on older Keepos). "author" isn't here: it was preset, so it reads as not selected. */
export const LEGACY_CATEGORIES = [
  "artist",
  "designer",
  "entrepreneur",
  "photographer",
  "influencer",
  "infoproductor",
  "education",
  "health",
  "food",
  "social_projects",
  "ecommerce",
  "marketing",
];

/** Preset on every new Keepo until now, so almost nobody picked it: treated as no field chosen. */
const UNSELECTED_LEGACY_CATEGORY = "author";

export type ParsedCategory =
  | { kind: "none" }
  | { kind: "legacy"; key: string }
  | { kind: "segment"; segment: string; specialty: string };

export const parseCategory = (value?: string): ParsedCategory => {
  if (!value || value === UNSELECTED_LEGACY_CATEGORY) {
    return { kind: "none" };
  }
  // "education", "health" and "food" were legacy keys and are segments now: read them as segments
  const [segment, specialty = ""] = value.split("/");
  if (CATEGORY_SEGMENTS.some(({ key }) => key === segment)) {
    return { kind: "segment", segment, specialty };
  }
  return LEGACY_CATEGORIES.includes(value) ? { kind: "legacy", key: value } : { kind: "none" };
};

export const buildCategory = (segment: string, specialty?: string) => (specialty ? `${segment}/${specialty}` : segment);

/** Readable field for people and the AI (e.g. "Beleza › Cabeleireiro"); undefined when none is chosen. */
export const categoryLabel = (value: string | undefined, other: string | undefined, t: TFunction) => {
  const parsed = parseCategory(value);
  if (parsed.kind === "legacy") {
    return t(`site.categories.${parsed.key}`);
  }
  if (parsed.kind !== "segment") {
    return undefined;
  }
  if (parsed.segment === OTHER_SEGMENT) {
    return other?.trim() || t(`site.categoryTree.${OTHER_SEGMENT}.label`);
  }
  const segmentLabel = t(`site.categoryTree.${parsed.segment}.label`);
  return parsed.specialty
    ? `${segmentLabel} › ${t(`site.categoryTree.${parsed.segment}.specialties.${parsed.specialty}`)}`
    : segmentLabel;
};

/**
 * The words to search free photos with for a Keepo's field of work (Pexels, /editor/style): its specialty
 * ("Barbearia"), else its segment, a legacy key's name or the user's own words; none without a field.
 */
export const categorySearch = (value: string | undefined, other: string | undefined, t: TFunction) => {
  const parsed = parseCategory(value);
  const words =
    parsed.kind === "legacy"
      ? t(`site.categories.${parsed.key}`)
      : parsed.kind !== "segment"
        ? undefined
        : parsed.segment === OTHER_SEGMENT
          ? other?.trim()
          : parsed.specialty
            ? t(`site.categoryTree.${parsed.segment}.specialties.${parsed.specialty}`)
            : t(`site.categoryTree.${parsed.segment}.label`);
  // "Cabeleireiro(a)", "Autor (a)": the word alone searches better
  return words?.replace(/\s*\((a|as|o|os)\)/gi, "").trim() || undefined;
};
